'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from './AuthProvider';
import { getSupabaseBrowserClient } from '../../lib/supabase';
import { startSubscriptionSync } from '../../lib/subscription-sync';
import {
  type SubscriptionTier,
  type SubscriptionState,
  type SubscriptionSnapshot,
  deriveSubscriptionState,
  normalizeSubscriptionTier,
} from '../../lib/subscription';

// ── Anonymous monthly usage (localStorage, resets each calendar month) ──

const ANON_USAGE_KEY = 'slidetoanki_anon_usage';

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function getAnonMonthlyUsage(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(ANON_USAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (parsed.month !== currentMonthKey()) return 0;
    return parsed.count ?? 0;
  } catch {
    return 0;
  }
}

export function incrementAnonMonthlyUsage(): number {
  const month = currentMonthKey();
  const current = getAnonMonthlyUsage();
  const next = current + 1;
  localStorage.setItem(ANON_USAGE_KEY, JSON.stringify({ month, count: next }));
  return next;
}

// ── Subscription context ──

const defaultState: SubscriptionState = deriveSubscriptionState('free', 0, 0, null, false, true);

const SubscriptionContext = createContext<SubscriptionState & { refreshSubscription: () => Promise<void> }>({
  ...defaultState,
  refreshSubscription: async () => {},
});

export function SubscriptionProvider({ children, initialSubscription }: {
  children: ReactNode;
  initialSubscription?: SubscriptionSnapshot | null;
}) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const initial = initialSubscription?.userId === userId ? initialSubscription : null;
  const posthog = usePostHog();
  const [tier, setTier] = useState<SubscriptionTier>(initial?.tier ?? 'free');
  const [generationsUsed, setGenerationsUsed] = useState(initial?.generationsUsed ?? 0);
  const [deckCount, setDeckCount] = useState(initial?.deckCount ?? 0);
  const [bonusGenerations, setBonusGenerations] = useState(initial?.bonusGenerations ?? 0);
  const [status, setStatus] = useState<SubscriptionState['status']>(initial?.status ?? null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(initial?.cancelAtPeriodEnd ?? false);
  const [loading, setLoading] = useState(!initial);
  const [resolvedUserId, setResolvedUserId] = useState<string | null | undefined>(initial?.userId);
  const requestRevision = useRef(0);
  const resolvedUserIdRef = useRef(initial?.userId);
  const prevTier = useRef<SubscriptionTier>(initial?.tier ?? 'free');

  const fetchSubscriptionData = useCallback(async () => {
    const revision = ++requestRevision.current;
    if (authLoading) return;
    const supabase = getSupabaseBrowserClient();
    if (!supabase || !userId) {
      setTier('free');
      setGenerationsUsed(getAnonMonthlyUsage());
      setDeckCount(0);
      setBonusGenerations(0);
      setStatus(null);
      setCancelAtPeriodEnd(false);
      setResolvedUserId(userId);
      resolvedUserIdRef.current = userId;
      setLoading(false);
      return;
    }

    try {
      const [subResult, usageResult, deckResult, creditResult] = await Promise.all([
        supabase.from('subscriptions').select('tier, status, cancel_at_period_end, stripe_customer_id').eq('user_id', userId).maybeSingle(),
        supabase.rpc('get_user_usage', { p_user_id: userId }),
        supabase.from('saved_decks').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('referral_credit_ledger').select('delta').eq('user_id', userId),
      ]);
      if (revision !== requestRevision.current) return;
      if (subResult.error) throw subResult.error;

      const newTier = normalizeSubscriptionTier(subResult.data?.tier);
      setTier(newTier);
      setStatus(subResult.data?.status || null);
      setCancelAtPeriodEnd(subResult.data?.cancel_at_period_end || false);
      setGenerationsUsed(usageResult.data ?? 0);
      setDeckCount(deckResult.count ?? 0);
      const nextBonusGenerations = (creditResult.data || []).reduce(
        (sum, row) => sum + Number(row.delta || 0),
        0,
      );
      setBonusGenerations(Math.max(0, nextBonusGenerations));

      // PostHog user properties
      posthog?.capture('$set', {
        $set: {
          subscription_tier: newTier,
          subscription_status: subResult.data?.status || 'none',
          stripe_customer_id: subResult.data?.stripe_customer_id || null,
          referral_bonus_credits: Math.max(0, nextBonusGenerations),
        },
      });

      // Track tier change
      if (prevTier.current !== newTier && prevTier.current !== 'free') {
        posthog?.capture('tier_changed', {
          from_tier: prevTier.current,
          to_tier: newTier,
        });
      }
      prevTier.current = newTier;
      resolvedUserIdRef.current = userId;
    } catch (err) {
      if (revision !== requestRevision.current) return;
      console.error('[Subscription] Failed to fetch:', err);
      // Keep an already verified plan through a transient background failure.
      // An unknown plan remains unresolved instead of flashing a Free badge.
    } finally {
      if (revision === requestRevision.current && resolvedUserIdRef.current === userId) {
        setResolvedUserId(userId);
        setLoading(false);
      }
    }
  }, [userId, authLoading, posthog]);

  useEffect(() => {
    fetchSubscriptionData();
    const client = getSupabaseBrowserClient();
    const stopSync = !authLoading && userId && client
      ? startSubscriptionSync(client, userId, fetchSubscriptionData) : undefined;
    return () => {
      stopSync?.();
      requestRevision.current += 1;
    };
  }, [fetchSubscriptionData, authLoading, userId]);

  const state = deriveSubscriptionState(
    tier,
    generationsUsed,
    deckCount,
    status,
    cancelAtPeriodEnd,
    // Never publish an unresolved plan or the previous account's subscription.
    authLoading || loading || resolvedUserId !== userId,
    bonusGenerations,
  );

  return (
    <SubscriptionContext.Provider value={{ ...state, refreshSubscription: fetchSubscriptionData }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
